import { ApiError } from "next/dist/server/api-utils";
import { toast } from "react-toastify";
import { create } from "zustand";
import { GetSavedListingsResponse, SavedState } from "./types";
import { ListingCardData, PurposeEnum, TypeEnum } from "@/types/types";
import { env } from "@/lib/env";
import { ChangeEvent } from "react";

const PAGE_SIZES = 24;

const SEARCHENDPOINTURL = env.NEXT_SEARCH_API_URL;
let debounceTimeout: ReturnType<typeof setTimeout>;

//what I would do is next:
//redo the state into one single object similar to the backend response
//keep fetch page and init first page
//change suggestion logic, remove it and keep it simple
//no more direct suggestion, we will change the searchbars a bit

interface ListingStore {
  state: SavedState;
  listingsLocale: string;
  initFirstPage: (
    data: GetSavedListingsResponse,
    locale: string,
    setCache: (listings: ListingCardData[]) => void
  ) => void;
  fetchPage: (
    locale: string,
    sessionId: string | null,
    pageNumber: number,
    setCache: (listings: ListingCardData[]) => void
  ) => Promise<void>;
  text: string;
  type: TypeEnum;
  purpose: PurposeEnum;
  onTextChange: (
    e: ChangeEvent<HTMLInputElement>,
    locale: string,
    sessionId: string | null,
    setCache: (listings: ListingCardData[]) => void
  ) => void;
  refetchState: (
    locale: string,
    sessionId: string | null,
    setCache: (listings: ListingCardData[]) => void
  ) => Promise<void>;
  setType: (type: TypeEnum) => void;
  setPurpose: (purpose: PurposeEnum) => void;
}

export const useSavedListingsStore = create<ListingStore>((set, get) => ({
  state: {
    listingsIds: [],
    total_count: 0,
    page_number: 1,
    isLoaded: false,
    pageLoading: true,
    total_number_of_pages: 1,
    fetched_at: 0,
    current_suggestion: {
      type: "",
      value: "",
      page_number: 1,
    },
  },
  listingsLocale: "en",
  initFirstPage: async (data, locale, setCache) => {
    const fetchedAt = get().state.fetched_at;
    if (fetchedAt === data.fetched_at) {
      return;
    }
    let pageListingsIds: string[];
    pageListingsIds = data.listings.map((listing) => listing.listing_id);

    setCache([...data.listings]);
    set((currentState) => ({
      ...currentState,
      state: {
        ...currentState.state,
        listingsIds: pageListingsIds,
        pageLoading: false,
        isLoading: false,
        isLoaded: true,
        fetched_at: data.fetched_at,
        page_number: data.page_number,
        total_count: data.total_count,
        total_number_of_pages: data.number_of_pages,
        current_suggestion: data.current_suggestion,
      },
      listingsLocale: locale,
      text: "",
      type: TypeEnum.DEFAULT,
      purpose: PurposeEnum.DEFAULT,
    }));
  },
  fetchPage: async (locale, sessionId, pageNumber, setCache) => {
    const isAlreadyLoading = get().state.pageLoading;
    if (isAlreadyLoading) {
      return;
    }
    if (!sessionId) {
      toast("Please reauthenticate before performing this action.", {
        type: "error",
      });
      return;
    }
    try {
      set((state) => ({
        ...state,
        pageLoading: true,
      }));
      const type = get().type;
      const purpose = get().purpose;
      const currentSuggestion = get().state.current_suggestion;
      const text = get().text;
      const response = await fetch(
        `${SEARCHENDPOINTURL}/getSavedListings?text=${text}&locale=${locale}&current_suggestion_type=${currentSuggestion.type}&current_suggestion_value=${currentSuggestion.value}&current_suggestion_page=${pageNumber}&type=${type}&purpose=${purpose}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionId,
          },
        }
      );

      if (response.ok) {
        const apiResponse = (await response.json()) as GetSavedListingsResponse;

        let pageListingsIds: string[];
        pageListingsIds = apiResponse.listings.map(
          (listing) => listing.listing_id
        );
        setCache([...apiResponse.listings]);
        set((currentState) => ({
          ...currentState,
          state: {
            ...currentState.state,
            listingsIds: pageListingsIds,
            pageLoading: false,
            fetched_at: apiResponse.fetched_at,
            page_number: apiResponse.page_number,
            total_count: apiResponse.total_count,
            total_number_of_pages: apiResponse.number_of_pages,
            current_suggestion: apiResponse.current_suggestion,
          },
        }));
      } else {
        const apiErr = (await response.json()) as ApiError;
        throw new Error(apiErr.message);
      }
    } catch (err) {
      set((state) => ({
        ...state,
        pageLoading: false,
      }));
      const errorMessage =
        err instanceof Error ? err.message : "An unexpected error occurred.";
      toast(
        errorMessage === "Failed to fetch"
          ? "Unable to get more saved listings."
          : errorMessage,
        {
          type: "error",
        }
      );
      return;
    }
  },
  text: "",
  type: TypeEnum.DEFAULT,
  purpose: PurposeEnum.DEFAULT,
  onTextChange: (e, locale, sessionId, setCache) => {
    e.preventDefault();
    const text = e.target.value;
    set({ text: text });
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }
    debounceTimeout = setTimeout(() => {
      set((currentState) => ({
        ...currentState,
        state: {
          ...currentState.state,
          current_suggestion: {
            value: "",
            type: "",
            page_number: 1,
          },
        },
      }));
      get().refetchState(locale, sessionId, setCache);
    }, 300);
  },
  refetchState: async (locale, sessionId, setCache) => {
    if (!sessionId) {
      toast("Please reauthenticate before performing this action.", {
        type: "error",
      });
      return;
    }
    await get().fetchPage(locale, sessionId, 1, setCache);
    // await Promise.all([]);
  },
  setType: (type: TypeEnum) => {
    set({
      type: type,
    });
  },
  setPurpose: (purpose: PurposeEnum) => {
    set({
      purpose: purpose,
    });
  },
}));

// getAutoSuggestion: async (text, locale) => {
//   if (debounceTimeout) clearTimeout(debounceTimeout);

//   if (text.length < 2) {
//     set({ suggestions: [], isSuggestionsOpen: false });
//     return;
//   }

//   debounceTimeout = setTimeout(async () => {
//     try {
//       const resp = await fetch(
//         `${SEARCHENDPOINTURL}/getAutocomplete?text=${encodeURIComponent(
//           text
//         )}&locale=${locale}`,
//         {
//           headers: {
//             "Content-Type": "application/json",
//             "x-viewer-lat": "",
//             "x-viewer-long": "",
//           },
//         }
//       );
//       if (resp.ok) {
//         const data = (await resp.json()) as {
//           locations_array: AutocompleteSuggestion[];
//         };
//         set({ suggestions: data.locations_array, isSuggestionsOpen: true });
//       } else {
//         const apiErr = (await resp.json()) as ApiError;
//         throw new Error(apiErr.message);
//       }
//     } catch (err) {
//       set({ suggestions: [], isSuggestionsOpen: false });
//       const errorMessage =
//         err instanceof Error ? err.message : "An unexpected error occurred.";
//       toast(
//         errorMessage === "Failed to fetch"
//           ? "Unable to get location suggestions."
//           : errorMessage,
//         {
//           type: "error",
//         }
//       );
//       return;
//     }
//   }, 300);
// },
